/** @jsx isolateComponent */

import type { ReactNode } from 'react';
import { Interpolation } from '@emotion/core';

import {
  isolateComponent,
  labelStyles,
  withModifier,
} from '@zapier/style-encapsulation';

import type { ShadowValue } from '@zapier/design-system';

import type { ServiceIconSize } from '../ServiceIcon';

type Props = {
  /**
   * Children to render inside of the group, usually either `ServiceIcon` or `ServiceIconShell`
   */
  children: ReactNode;
  /**
   * The height of the grouping, used to calculate the border radius.
   */
  height: ServiceIconSize;
  /**
   * Applies a shadow to the entire group.
   */
  shadow?: ShadowValue;
};

const Styles = labelStyles('ServiceIconGroup', {
  root: (props: Props) => [
    {
      borderRadius: Math.ceil(0.25 * props.height),
      display: 'inline-flex',
      flexDirection: 'row',
    },

    props.shadow &&
      withModifier('shadowed', {
        boxShadow: props.shadow,
      }),
  ],
});

/**
 * The `ServiceIconGroup` component is used to render a collection of `ServiceIcons` as a grouping.
 * Its primary purpose is to provide a wrapping component that can be styled (e.g. applying a shadow).
 */
export const ServiceIconGroup = (props: Props) => {
  return <div css={Styles.root(props) as Interpolation}>{props.children}</div>;
};
